import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { SocialSubNav } from "@/components/admin/SocialSubNav";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  listSocialCompliance,
  rescanSocialPost,
  rescanAllSocial,
} from "@/lib/social-compliance.functions";

export const Route = createFileRoute("/admin/social/compliance")({
  component: SocialCompliancePage,
  head: () => ({ meta: [{ title: "Social compliance · Admin" }, { name: "robots", content: "noindex" }] }),
});

type Finding = {
  source: string;
  severity: "error" | "warn";
  label: string;
  ruleId: string | null;
};

function statusColor(s?: string | null) {
  if (s === "blocked") return "destructive";
  if (s === "warn") return "secondary";
  return "outline";
}

function SocialCompliancePage() {
  const [filter, setFilter] = useState<"all" | "blocked" | "warn" | "clean">("all");
  const list = useServerFn(listSocialCompliance);
  const rescanOne = useServerFn(rescanSocialPost);
  const rescanAll = useServerFn(rescanAllSocial);
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["social-compliance"],
    queryFn: () => list(),
  });

  const rescanOneM = useMutation({
    mutationFn: (id: string) => rescanOne({ data: { id } }),
    onSuccess: () => {
      toast.success("Rescanned");
      qc.invalidateQueries({ queryKey: ["social-compliance"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Rescan failed"),
  });

  const rescanAllM = useMutation({
    mutationFn: () => rescanAll(),
    onSuccess: (r: any) =>
      toast.success(`Rescanned ${r.scanned}. Blocked ${r.blocked}, warned ${r.warned}.`),
    onSettled: () => qc.invalidateQueries({ queryKey: ["social-compliance"] }),
    onError: (e: any) => toast.error(e?.message ?? "Rescan failed"),
  });

  const rows = (data?.rows ?? []).filter((r: any) => {
    if (filter === "all") return true;
    if (filter === "clean") return !r.compliance_status || r.compliance_status === "clean";
    return r.compliance_status === filter;
  });

  return (
    <div className="container max-w-6xl py-8">
      <SocialSubNav />
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Compliance</h1>
          <p className="text-sm text-muted-foreground">
            Every draft, schedule and publish runs through the lexicon, net-impression, and URL guards. Findings here reflect the last save or scan.
          </p>
        </div>
        <Button onClick={() => rescanAllM.mutate()} disabled={rescanAllM.isPending}>
          {rescanAllM.isPending ? "Rescanning…" : "Rescan all"}
        </Button>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 md:grid-cols-4">
        {(["all", "blocked", "warn", "clean"] as const).map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            className={`rounded-lg border p-3 text-left transition ${
              filter === k ? "border-primary bg-primary/5" : "border-border"
            }`}
          >
            <div className="text-xs uppercase text-muted-foreground">{k}</div>
            <div className="text-2xl font-semibold">
              {k === "all"
                ? data?.counts.total ?? 0
                : (data?.counts as any)?.[k] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {isLoading ? (
        <div className="py-10 text-center text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-10 text-center text-muted-foreground">Nothing to review.</div>
      ) : (
        <div className="space-y-3">
          {rows.map((r: any) => {
            const findings: Finding[] = Array.isArray(r.compliance_findings)
              ? r.compliance_findings
              : [];
            return (
              <Card key={r.id} className="p-4">
                <div className="mb-2 flex items-center gap-2">
                  <Badge variant={statusColor(r.compliance_status) as any}>
                    {r.compliance_status ?? "clean"}
                  </Badge>
                  <Badge variant="outline">{r.status}</Badge>
                  <span className="text-xs text-muted-foreground">
                    Updated {new Date(r.updated_at).toLocaleString()}
                  </span>
                  <div className="ml-auto">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => rescanOneM.mutate(r.id)}
                      disabled={rescanOneM.isPending}
                    >
                      Rescan
                    </Button>
                  </div>
                </div>
                <p className="whitespace-pre-wrap text-sm">{r.body}</p>
                {r.link_url && (
                  <a
                    href={r.link_url}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-2 block text-xs text-primary underline"
                  >
                    {r.link_url}
                  </a>
                )}
                {findings.length > 0 && (
                  <ul className="mt-3 space-y-1 border-t pt-3 text-xs">
                    {findings.map((f, i) => (
                      <li key={i} className="flex items-start gap-2">
                        <Badge
                          variant={f.severity === "error" ? "destructive" : "secondary"}
                          className="shrink-0"
                        >
                          {f.severity}
                        </Badge>
                        <span>
                          <span className="font-semibold">{f.source}</span>
                          {f.ruleId ? `/${f.ruleId}` : ""} — {f.label}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
